import { useMemo, useState } from 'react'
import { errorHandler } from '../../../../services/util/error.service'
import {
  deleteCancelMdfe,
  getMdfesByTp,
  postEncerrarMdfe,
  postSyncMdfe,
} from '../../../../services/api/https/deliveries/mdfe.http'
import { getPeriodoMesAtual } from '../../../../services/util/date.service'
import { urlDownloadMdfe } from '../../../../services/api/routes/mdfe.routes'

const INITIAL_STATE = {
  errorMessage: null,
  loadingMessage: null,
  mdfes: [],
  filteredMdfes: [],
  modal: { visible: false, mdfe: null },
  modalEncerrar: { visible: false, mdfe: null },
  selectedMdfe: null,
  selectedStatus: [],
  selecionarTodos: false,
  periodo: getPeriodoMesAtual(),
}

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  FETCH: 'Buscando MDFes',
  DELETE: 'Cancelando MDFe',
  SUCCESS_DELETE: 'MDFe cancelado com sucesso',
  ENCERRAR: 'Encerrando MDFe',
  SUCCESS_ENCERRAR_MDFE: 'MDFe encerrado com sucesso',
  SYNC: 'Sincronizando status do MDFe',
  SUCCESS_SYNC: 'Status do MDFe sincronizado com sucesso',
}

const aplicarFiltros = (mdfes, selectedStatus) => {
  const statusValues = selectedStatus || []
  return (mdfes || []).filter(m => {
    if (statusValues.length && !statusValues.includes(m.status)) return false
    return true
  })
}

export default function useGestaoMdfes() {
  const [state, setState] = useState({ ...INITIAL_STATE })

  const localDispatch = useMemo(() => {
    const fetchMdfes = async (tp_id, pInicial, pFinal) => {
      if (!tp_id) {
        setState(prevState => ({ ...prevState, errorMessage: 'Selecione um TP' }))
        return
      }

      if (!pInicial || !pFinal) {
        setState(prevState => ({
          ...prevState,
          errorMessage: 'Período inicial e final são obrigatórios',
        }))
        return
      }

      try {
        setState(prevState => ({ ...prevState, loadingMessage: LOAD_MESSAGE.FETCH }))
        const mdfes = await getMdfesByTp(tp_id, pInicial, pFinal)

        setState(prev => ({
          ...prev,
          loadingMessage: LOAD_MESSAGE.NONE,
          mdfes,
          filteredMdfes: aplicarFiltros(mdfes, prev.selectedStatus),
          periodo: { dataInicial: pInicial, dataFinal: pFinal },
        }))
      } catch (error) {
        setState(prevState => ({
          ...prevState,
          errorMessage: errorHandler(error),
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      }
    }

    return {
      resetState() {
        setState(prev => ({ ...prev, loadingMessage: null, errorMessage: null }))
      },

      // Events
      async onInit(tp_id, pInicial, pFinal) {
        await fetchMdfes(tp_id, pInicial, pFinal)
      },

      onPesquisar(tp_id, pInicial, pFinal) {
        fetchMdfes(tp_id, pInicial, pFinal)
      },

      onMenuItemClick(item, mdfe) {
        const menus = {
          'Cancelar MDFe': () =>
            setState(prevState => ({ ...prevState, modal: { visible: true, mdfe } })),
          Encerrar: () =>
            setState(prevState => ({ ...prevState, modalEncerrar: { visible: true, mdfe } })),
          'Sincronizar Status': () =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.SYNC,
              selectedMdfe: mdfe,
            })),
          DAMDFE: () => window.open(mdfe.caminho_damdfe, '_blank'),
          XML: () => window.open(mdfe.caminho_xml, '_blank'),
          'XML Cancelamento': () => window.open(mdfe.caminho_xml_cancelamento, '_blank'),
          'XML Encerramento': () => window.open(mdfe.caminho_xml_encerramento, '_blank'),
        }
        if (menus[item]) {
          menus[item]()
        }
      },

      onItemClicked(mdfe) {
        setState(prev => {
          const filteredMdfes = prev.filteredMdfes.map(m =>
            m.id === mdfe.id ? { ...m, selected: !m.selected } : m,
          )
          return { ...prev, filteredMdfes }
        })
      },

      onModalDeleteClose() {
        setState(prevState => ({
          ...prevState,
          modal: { visible: false, mdfe: null },
        }))
      },

      onModalDeleteSave(mdfe) {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.DELETE,
          modal: { visible: false, mdfe: null },
          selectedMdfe: mdfe,
        }))
      },

      deleteMdfe(mdfe) {
        deleteCancelMdfe({ ref: mdfe.ref, justificativa: mdfe.justificativa })
          .then(() =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.SUCCESS_DELETE,
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              errorMessage: errorHandler(err),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
      },

      syncMdfe(mdfe) {
        postSyncMdfe({ ref: mdfe.ref })
          .then(() =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.SUCCESS_SYNC,
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              errorMessage: errorHandler(err),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
      },

      onModalEncerrarClose() {
        setState(prevState => ({
          ...prevState,
          modalEncerrar: { visible: false, mdfe: null },
        }))
      },

      onModalEncerrarSave(mdfe) {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.ENCERRAR,
          modalEncerrar: { visible: false, mdfe: null },
          selectedMdfe: mdfe,
        }))
      },

      encerrarMdfe(mdfe) {
        if (mdfe.status !== 'autorizado')
          return setState(prevState => ({
            ...prevState,
            errorMessage: 'MDFe precisa estar AUTORIZADO',
          }))

        const body = {
          ref: mdfe.ref,
          sigla_uf: mdfe.sigla_uf,
          nome_municipio: mdfe.nome_municipio,
        }

        postEncerrarMdfe(body)
          .then(() => {
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.SUCCESS_ENCERRAR_MDFE,
            }))
          })
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              errorMessage: errorHandler(err),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
      },

      onModalError(error) {
        setState(prevState => ({
          ...prevState,
          errorMessage: errorHandler(error),
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      },

      handleStatusFiltroChange(selectedStatus) {
        setState(prevState => {
          const safeSelectedStatus = selectedStatus || []
          return {
            ...prevState,
            selectedStatus: safeSelectedStatus,
            filteredMdfes: aplicarFiltros(prevState.mdfes, safeSelectedStatus),
          }
        })
      },
      onSelecionarTodosChange(value) {
        setState(prev => {
          return {
            ...prev,
            filteredMdfes: prev.filteredMdfes.map(e => ({ ...e, selected: value })),
            selecionarTodos: value,
          }
        })
      },
      onDownloadMdfe(mdfes) {
        if (!mdfes.length) {
          setState(prevState => ({
            ...prevState,
            errorMessage: 'Selecione ao menos um MDFe para fazer o download',
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
          return
        }

        window.open(urlDownloadMdfe(mdfes.map(m => m.id)), '_blank')
      },
    }
  }, [])

  return { state, localDispatch }
}
